import { Injectable, Renderer2, RendererFactory2, OnDestroy } from '@angular/core';
import { resolveApiErrorMessage } from '../utils/api-error.util';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

/**
 * Interface para tipagem do Bootstrap Toast
 */
interface BootstrapToast {
  show(): void;
  hide(): void;
  dispose(): void;
}

/**
 * Interface para opções do Bootstrap Toast
 */
interface BootstrapToastOptions {
  autohide?: boolean;
  delay?: number;
}

/**
 * Configuração padrão para toasts
 */
const DEFAULT_TOAST_DELAY = 5000;
const MAX_RETRY_ATTEMPTS = 10;
const RETRY_DELAY = 100;

@Injectable({
  providedIn: 'root'
})
export class ToastService implements OnDestroy {
  private toastContainer: HTMLElement | null = null;
  private renderer: Renderer2;
  private activeTimeouts: Set<number> = new Set();

  constructor(private rendererFactory: RendererFactory2) {
    this.renderer = this.rendererFactory.createRenderer(null, null);
  }

  ngOnDestroy(): void {
    // Limpar todos os timeouts ativos para evitar memory leaks
    this.activeTimeouts.forEach(timeoutId => {
      clearTimeout(timeoutId);
    });
    this.activeTimeouts.clear();
  }

  /**
   * Garante que o container de toast existe no DOM
   * Usa Renderer2 para manipulação segura do DOM (boas práticas Angular)
   * Cria o container apenas quando necessário (lazy initialization)
   */
  private ensureToastContainer(): void {
    if (typeof document === 'undefined' || !document.body) {
      return;
    }

    if (!this.toastContainer) {
      this.toastContainer = document.getElementById('toast-container');
      
      if (!this.toastContainer) {
        // Criar container se não existir usando Renderer2
        this.toastContainer = this.renderer.createElement('div');
        this.renderer.setProperty(this.toastContainer, 'id', 'toast-container');
        this.renderer.addClass(this.toastContainer, 'toast-container');
        this.renderer.addClass(this.toastContainer, 'position-fixed');
        this.renderer.addClass(this.toastContainer, 'top-0');
        this.renderer.addClass(this.toastContainer, 'end-0');
        this.renderer.addClass(this.toastContainer, 'p-3');
        this.renderer.setStyle(this.toastContainer, 'z-index', '9999');
        this.renderer.appendChild(document.body, this.toastContainer);
      }
    }
  }

  /**
   * Exibe um toast de sucesso
   */
  showSuccess(message: string, title: string = 'Sucesso'): void {
    this.showToast(message, title, 'success');
  }

  /**
   * Exibe um toast de erro
   */
  showError(message: string, title: string = 'Erro'): void {
    this.showToast(message, title, 'error');
  }

  /**
   * Exibe o erro da API preferindo a mensagem que ela mandou, e caindo em `fallback` só quando não
   * veio nada aproveitável.
   *
   * É assíncrono porque o corpo de erro de download (`responseType: 'blob'`) só pode ser lido via
   * `Blob.text()`. O toast aparece no microtask seguinte, o que é imperceptível e vale o preço:
   * sem isso, todo erro de exportação perde a mensagem do backend.
   */
  showApiError(error: unknown, fallback: string, title: string = 'Erro'): void {
    resolveApiErrorMessage(error)
      .then((message) => this.showToast(message || fallback, title, 'error'))
      .catch(() => this.showToast(fallback, title, 'error'));
  }

  /**
   * Exibe um toast de aviso
   */
  showWarning(message: string, title: string = 'Aviso'): void {
    this.showToast(message, title, 'warning');
  }

  /**
   * Exibe um toast de informação
   */
  showInfo(message: string, title: string = 'Informação'): void {
    this.showToast(message, title, 'info');
  }

  /**
   * Exibe um toast genérico
   * Usa Renderer2 para manipulação segura do DOM
   * @param message - Mensagem a ser exibida (será sanitizada)
   * @param title - Título do toast (será sanitizado)
   * @param type - Tipo do toast
   * @param retryAttempt - Número da tentativa atual (para evitar recursão infinita)
   */
  private showToast(message: string, title: string, type: ToastType, retryAttempt: number = 0): void {
    // Verificar se estamos em um ambiente com DOM disponível
    if (typeof document === 'undefined') {
      console.warn('ToastService: Executando em SSR, toast não pode ser exibido');
      return;
    }

    // Validar parâmetros
    if (!message || !title) {
      console.warn('ToastService: Mensagem ou título não fornecidos');
      return;
    }

    // Garantir que o container existe antes de criar o toast
    this.ensureToastContainer();
    
    if (!this.toastContainer) {
      // Se ainda não temos o container, tentar novamente (com limite de tentativas)
      if (retryAttempt < MAX_RETRY_ATTEMPTS) {
        const timeoutId = window.setTimeout(() => {
          this.activeTimeouts.delete(timeoutId);
          this.showToast(message, title, type, retryAttempt + 1);
        }, RETRY_DELAY);
        this.activeTimeouts.add(timeoutId);
      } else {
        console.error('ToastService: Não foi possível criar o container após múltiplas tentativas');
      }
      return;
    }

    try {
      // Criar elemento do toast usando Renderer2
      const toastId = `toast-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
      const toast = this.renderer.createElement('div');
      this.renderer.setProperty(toast, 'id', toastId);
      this.renderer.addClass(toast, 'toast');
      this.renderer.addClass(toast, 'align-items-center');
      this.renderer.addClass(toast, 'text-white');
      this.renderer.addClass(toast, 'border-0');
      this.renderer.addClass(toast, this.getBackgroundClass(type));
      
      this.renderer.setAttribute(toast, 'role', 'alert');
      this.renderer.setAttribute(toast, 'aria-live', 'assertive');
      this.renderer.setAttribute(toast, 'aria-atomic', 'true');
      
      // Criar estrutura do toast
      const flexContainer = this.renderer.createElement('div');
      this.renderer.addClass(flexContainer, 'd-flex');
      
      const toastBody = this.renderer.createElement('div');
      this.renderer.addClass(toastBody, 'toast-body');
      
      const innerFlex = this.renderer.createElement('div');
      this.renderer.addClass(innerFlex, 'd-flex');
      this.renderer.addClass(innerFlex, 'align-items-center');
      
      // Ícone
      const icon = this.renderer.createElement('i');
      // Adicionar classes do ícone separadamente (getIcon retorna múltiplas classes)
      const iconClasses = this.getIcon(type).split(' ');
      iconClasses.forEach(iconClass => {
        if (iconClass.trim()) {
          this.renderer.addClass(icon, iconClass.trim());
        }
      });
      this.renderer.addClass(icon, 'me-2');
      this.renderer.addClass(icon, 'fs-18');
      
      // Conteúdo (sanitizar para prevenir XSS)
      const content = this.renderer.createElement('div');
      const titleElement = this.renderer.createElement('strong');
      // Usar createText para sanitizar automaticamente e prevenir XSS
      this.renderer.appendChild(titleElement, this.renderer.createText(this.sanitizeText(title)));
      
      const messageElement = this.renderer.createElement('div');
      this.renderer.addClass(messageElement, 'fs-14');
      this.renderer.appendChild(messageElement, this.renderer.createText(this.sanitizeText(message)));
      
      this.renderer.appendChild(content, titleElement);
      this.renderer.appendChild(content, messageElement);
      
      this.renderer.appendChild(innerFlex, icon);
      this.renderer.appendChild(innerFlex, content);
      this.renderer.appendChild(toastBody, innerFlex);
      
      // Botão de fechar
      const closeButton = this.renderer.createElement('button');
      this.renderer.setAttribute(closeButton, 'type', 'button');
      this.renderer.addClass(closeButton, 'btn-close');
      this.renderer.addClass(closeButton, 'btn-close-white');
      this.renderer.addClass(closeButton, 'me-2');
      this.renderer.addClass(closeButton, 'm-auto');
      this.renderer.setAttribute(closeButton, 'data-bs-dismiss', 'toast');
      this.renderer.setAttribute(closeButton, 'aria-label', 'Close');
      
      this.renderer.appendChild(flexContainer, toastBody);
      this.renderer.appendChild(flexContainer, closeButton);
      this.renderer.appendChild(toast, flexContainer);
      this.renderer.appendChild(this.toastContainer, toast);
      
      // Inicializar o toast do Bootstrap - usar requestAnimationFrame para garantir que o DOM está pronto
      requestAnimationFrame(() => {
        const Bootstrap = (window as any).bootstrap;
        if (Bootstrap && Bootstrap.Toast) {
          try {
            const options: BootstrapToastOptions = {
              autohide: true,
              delay: DEFAULT_TOAST_DELAY
            };
            
            const bsToast: BootstrapToast = new Bootstrap.Toast(toast, options);
            bsToast.show();
            
            // Remover o elemento do DOM após ser escondido
            const cleanup = () => {
              if (toast.parentNode && this.toastContainer) {
                this.renderer.removeChild(this.toastContainer, toast);
              }
            };
            toast.addEventListener('hidden.bs.toast', cleanup, { once: true });
          } catch (error) {
            console.error('ToastService: Erro ao inicializar toast do Bootstrap', error);
            // Fallback: mostrar o toast mesmo sem Bootstrap
            this.showFallbackToast(toast);
          }
        } else {
          console.warn('ToastService: Bootstrap não está disponível, usando fallback');
          // Fallback: mostrar o toast mesmo sem Bootstrap
          this.showFallbackToast(toast);
        }
      });
    } catch (error) {
      console.error('ToastService: Erro ao exibir toast', error);
    }
  }

  /**
   * Obtém a classe de background baseada no tipo
   */
  private getBackgroundClass(type: ToastType): string {
    switch (type) {
      case 'success':
        return 'bg-success';
      case 'error':
        return 'bg-danger';
      case 'warning':
        return 'bg-warning';
      case 'info':
        return 'bg-info';
      default:
        return 'bg-primary';
    }
  }

  /**
   * Obtém o ícone baseado no tipo
   */
  private getIcon(type: ToastType): string {
    switch (type) {
      case 'success':
        return 'ti ti-check-circle';
      case 'error':
        return 'ti ti-x-circle';
      case 'warning':
        return 'ti ti-alert-triangle';
      case 'info':
        return 'ti ti-info-circle';
      default:
        return 'ti ti-bell';
    }
  }

  /**
   * Exibe toast usando fallback (sem Bootstrap)
   * @param toast - Elemento do toast
   */
  private showFallbackToast(toast: HTMLElement): void {
    this.renderer.addClass(toast, 'show');
    const timeoutId = window.setTimeout(() => {
      this.activeTimeouts.delete(timeoutId);
      if (toast.parentNode && this.toastContainer) {
        this.renderer.removeChild(this.toastContainer, toast);
      }
    }, DEFAULT_TOAST_DELAY);
    this.activeTimeouts.add(timeoutId);
  }

  /**
   * Sanitiza texto para prevenir XSS
   * @param text - Texto a ser sanitizado
   * @returns Texto sanitizado
   */
  private sanitizeText(text: string): string {
    // O createText do Renderer2 já faz sanitização, mas vamos garantir
    // removendo tags HTML potencialmente perigosas
    const div = document.createElement('div');
    div.textContent = text;
    return div.textContent || '';
  }
}

